import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mountain, MapPin, ArrowRight } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function CragsListPage() {
  const { t, getTranslatedContent, language } = useLanguage();
  const [crags, setCrags] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCrags = async () => {
      try {
        const res = await axios.get(`${API}/crags`);
        setCrags(res.data || []);
      } catch (error) {
        console.error('Error fetching crags:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCrags();
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div data-testid="crags-list-page">
      {/* Hero Section */}
      <section className="relative py-32 bg-[#0A0A0A]">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Mountain className="w-16 h-16 text-[#FF6D00] mx-auto mb-6" />
            <h1 className="heading-xl text-white mb-6">
              {language === 'it' ? 'Le Nostre Falesie' : 'Our Crags'}
            </h1>
            <p className="body-lg text-gray-400 max-w-2xl mx-auto">
              {language === 'it' 
                ? 'Esplora le falesie del territorio di Caltavuturo, dalle storiche pareti di Gazzara ai settori più recenti.'
                : 'Explore the crags of Caltavuturo area, from the historic walls of Gazzara to the newest sectors.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Crags Grid */}
      <section className="section-padding">
        <div className="container-custom">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#FF6D00]"></div>
            </div>
          ) : (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {crags.map((crag) => (
                <motion.div
                  key={crag.id}
                  variants={itemVariants}
                  className="group"
                >
                  <Link to={`/crags/${crag.slug}`} className="block" data-testid={`crag-card-${crag.slug}`}>
                    <div className="relative aspect-[4/3] overflow-hidden rounded-xl shadow-lg">
                      <img 
                        src={crag.images?.[0] || 'https://images.unsplash.com/photo-1522163182402-834f871fd851?w=600'}
                        alt={crag.name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                      
                      {/* Content Overlay */}
                      <div className="absolute bottom-0 left-0 right-0 p-6">
                        <h2 className="font-heading text-2xl lg:text-3xl text-white uppercase mb-3">
                          {crag.name}
                        </h2>
                        
                        <div className="flex flex-wrap items-center gap-4 text-white/80 text-sm mb-4">
                          <span className="flex items-center gap-1">
                            <Mountain className="w-4 h-4" />
                            {crag.total_routes || 0} {t('routes')}
                          </span>
                          <span>{crag.grade_range || '-'}</span>
                          {crag.location?.city && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {crag.location.city}
                            </span>
                          )}
                        </div>

                        <p className="text-white/70 text-sm line-clamp-2 mb-4">
                          {getTranslatedContent(crag.description)}
                        </p>

                        <div className="flex items-center text-[#FF6D00] font-semibold group-hover:translate-x-2 transition-transform">
                          {language === 'it' ? 'Scopri di più' : 'Learn more'}
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          )}

          {!loading && crags.length === 0 && (
            <div className="text-center py-20">
              <Mountain className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">
                {language === 'it' ? 'Nessuna falesia disponibile' : 'No crags available'}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Stats Section */}
      {crags.length > 0 && (
        <section className="py-16 bg-[#0A0A0A]">
          <div className="container-custom">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-4xl lg:text-5xl font-heading text-[#FF6D00] mb-2">
                  {crags.length}
                </div>
                <div className="text-white/70">
                  {language === 'it' ? 'Falesie' : 'Crags'}
                </div>
              </div>
              <div>
                <div className="text-4xl lg:text-5xl font-heading text-[#FF6D00] mb-2">
                  {crags.reduce((acc, c) => acc + (c.total_routes || 0), 0)}
                </div>
                <div className="text-white/70">
                  {language === 'it' ? 'Vie totali' : 'Total routes'}
                </div>
              </div>
              <div>
                <div className="text-4xl lg:text-5xl font-heading text-[#FF6D00] mb-2">
                  4A-8C
                </div>
                <div className="text-white/70">
                  {language === 'it' ? 'Range difficoltà' : 'Grade range'}
                </div>
              </div>
              <div>
                <div className="text-4xl lg:text-5xl font-heading text-[#FF6D00] mb-2">
                  20+
                </div>
                <div className="text-white/70">
                  {language === 'it' ? 'Anni di storia' : 'Years of history'}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
